import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Download,
  Eye,
  Printer,
  Calendar,
  IndianRupee,
  TrendingUp,
  CreditCard,
  RefreshCw,
  Search,
  CheckCircle2,
  Clock,
  X,
  Building2,
  User,
  ShieldCheck,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal as Filters,
  BarChart2
} from 'lucide-react';
import api from '../../../api/axios';
import { toast } from 'react-hot-toast';
import { useLoading } from '../../../context/LoadingContext';
import styles from './EmployeeSalarySlips.module.css';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import Pagination from '../../../components/Common/Pagination/Pagination';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import NoData from '../../../components/Common/NoData/NoData';

const EmployeeSalarySlips = () => {
  const { showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [payrolls, setPayrolls] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 15
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  // Floating Action dropdown menu state
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Modals
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [previewSlipNumber, setPreviewSlipNumber] = useState('');

  // Close floating action dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (id, e) => {
    e.stopPropagation();
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuHeight = 160;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top;
      let transform = 'none';

      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        top = rect.top - 4;
        transform = 'translateY(-100%)';
      } else {
        top = rect.bottom + 4;
        transform = 'none';
      }

      setMenuPosition({
        top,
        left: Math.max(10, rect.right - 160),
        transform
      });
      setOpenMenuIndex(id);
    }
  };

  const fetchMyPayrolls = useCallback(async (page = 1, isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      showLoader();
    }

    try {
      const params = {
        page,
        per_page: pagination.per_page
      };
      if (selectedYear) {
        params.year = selectedYear;
      }

      const response = await api.get('/payroll/my-payrolls', { params });
      if (response.data.success) {
        const paginatedData = response.data.data;
        setPayrolls(paginatedData.data || []);
        setPagination({
          current_page: paginatedData.current_page || 1,
          last_page: paginatedData.last_page || 1,
          total: paginatedData.total || 0,
          per_page: paginatedData.per_page || 15
        });
      }
    } catch (error) {
      console.error('Error fetching salary slips:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch salary slips');
    } finally {
      if (isRefresh) {
        setIsRefreshing(false);
      } else {
        hideLoader();
      }
    }
  }, [pagination.per_page, selectedYear, showLoader, hideLoader]);

  useEffect(() => {
    fetchMyPayrolls(1);
  }, [fetchMyPayrolls]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchMyPayrolls(page);
    }
  };

  const resetFilters = () => {
    setSelectedYear('');
    setStatusFilter('');
  };

  const handleDownloadPdf = async (payroll) => {
    setOpenMenuIndex(null);
    showLoader();
    try {
      const response = await api.get(`/payroll/${payroll.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Salary_Slip_${payroll.payroll_number || payroll.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Salary slip downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download salary slip');
    } finally {
      hideLoader();
    }
  };

  const handlePreviewPdf = async (payroll) => {
    setOpenMenuIndex(null);
    showLoader();
    try {
      const response = await api.get(`/payroll/${payroll.id}/pdf/preview`, {
        responseType: 'blob'
      });
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      setPdfPreviewUrl(fileURL);
      setPreviewSlipNumber(payroll.payroll_number || payroll.id);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to open PDF preview');
    } finally {
      hideLoader();
    }
  };

  const closePdfPreview = () => {
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPreviewSlipNumber('');
    }
  };

  const formatCurrency = (amount) => {
    const val = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(val);
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[(parseInt(monthNumber, 10) - 1)] || monthNumber;
  };

  const getStatusBadge = (status) => {
    const lower = String(status || '').toLowerCase();
    if (lower === 'paid') {
      return (
        <span className={`${styles.statusBadge} ${styles.statusPaid}`}>
          <CheckCircle2 size={12} /> PAID
        </span>
      );
    }
    if (lower === 'approved') {
      return (
        <span className={`${styles.statusBadge} ${styles.statusApproved}`}>
          <ShieldCheck size={12} /> APPROVED
        </span>
      );
    }
    if (lower === 'generated') {
      return (
        <span className={`${styles.statusBadge} ${styles.statusGenerated}`}>
          <Clock size={12} /> GENERATED
        </span>
      );
    }
    return (
      <span className={`${styles.statusBadge} ${styles.statusDraft}`}>
        {(status || 'Draft').toUpperCase()}
      </span>
    );
  };

  // Client-side search and status filtering
  const filteredPayrolls = payrolls.filter((item) => {
    const monthName = getMonthName(item.month).toLowerCase();
    const payrollNum = (item.payroll_number || '').toLowerCase();
    const yearStr = String(item.year || '');
    const term = searchTerm.toLowerCase();

    const matchesSearch = !searchTerm || monthName.includes(term) || payrollNum.includes(term) || yearStr.includes(term);
    const matchesYear = !selectedYear || String(item.year) === String(selectedYear);
    const matchesStatus = !statusFilter || String(item.status).toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesYear && matchesStatus;
  });

  // Summary Metrics calculations
  const totalSlipsCount = pagination.total || payrolls.length;
  const latestNetSalary = payrolls.length > 0 ? parseFloat(payrolls[0].net_salary || 0) : 0;
  const totalEarned = payrolls.reduce((acc, curr) => acc + parseFloat(curr.net_salary || 0), 0);
  const totalDeductions = payrolls.reduce((acc, curr) => acc + parseFloat(curr.total_deductions || 0), 0);

  // Available Years
  const currentYear = new Date().getFullYear();
  const yearOptions = [
    { value: String(currentYear), label: String(currentYear) },
    { value: String(currentYear - 1), label: String(currentYear - 1) },
    { value: String(currentYear - 2), label: String(currentYear - 2) },
    { value: String(currentYear - 3), label: String(currentYear - 3) }
  ];

  return (
    <div className={styles.container}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles.contentWrapper}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles.mainContent}>
          <Breadcrumbs customItems={[
            { label: 'Employee Space', path: '/employees/dashboard' },
            { label: 'Salary Slips', path: '/employees/my-salary-slips' }
          ]} />

          {/* Page Header */}
          <div className={styles.pageHeader}>
            <div className={styles.headerTitleSection}>
              <h1>Employee Salary Slips</h1>
              <p>View, download, and track your monthly salary breakdown and disbursement records.</p>
            </div>
            <div className={styles.headerActions}>
              <button
                className={styles.refreshBtn}
                onClick={() => fetchMyPayrolls(pagination.current_page, true)}
                disabled={isRefreshing}
              >
                <RefreshCw size={16} className={isRefreshing ? styles.spinning : ''} />
                <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          {/* Summary Toggle (Mobile only - same as Purchase Orders) */}
          <button
            className={styles.summaryToggle}
            onClick={() => setIsSummaryOpen(!isSummaryOpen)}
          >
            <div className={styles.summaryToggleLeft}>
              <BarChart2 size={20} color="#f97316" />
              <div className={styles.summaryToggleText}>
                <span className={styles.summaryToggleTitle}>Summary Overview</span>
                <span className={styles.summaryToggleHint}>
                  {isSummaryOpen ? 'Tap to collapse statistics' : 'Tap to view statistics'}
                </span>
              </div>
            </div>
            {isSummaryOpen ? <ChevronUp size={20} color="#94a3b8" /> : <ChevronDown size={20} color="#94a3b8" />}
          </button>

          {/* Metrics Overview Cards */}
          <div className={`${styles.metricsGrid} ${isSummaryOpen ? styles.summaryExpanded : styles.summaryCollapsed}`}>
            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconBlue}`}>
                <FileText size={24} />
              </div>
              <div className={styles.metricInfo}>
                <span className={styles.metricLabel}>Total Slips</span>
                <span className={styles.metricValue}>{totalSlipsCount}</span>
                <span className={styles.metricSub}>Available payslips</span>
              </div>
            </div>

            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconGreen}`}>
                <IndianRupee size={24} />
              </div>
              <div className={styles.metricInfo}>
                <span className={styles.metricLabel}>Latest Take-Home</span>
                <span className={styles.metricValue}>{formatCurrency(latestNetSalary)}</span>
                <span className={styles.metricSub}>
                  {payrolls.length > 0 ? `${getMonthName(payrolls[0].month)} ${payrolls[0].year}` : 'N/A'}
                </span>
              </div>
            </div>

            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconPurple}`}>
                <TrendingUp size={24} />
              </div>
              <div className={styles.metricInfo}>
                <span className={styles.metricLabel}>Total Disbursed</span>
                <span className={styles.metricValue}>{formatCurrency(totalEarned)}</span>
                <span className={styles.metricSub}>From retrieved records</span>
              </div>
            </div>

            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconOrange}`}>
                <CreditCard size={24} />
              </div>
              <div className={styles.metricInfo}>
                <span className={styles.metricLabel}>Total Deductions</span>
                <span className={styles.metricValue}>{formatCurrency(totalDeductions)}</span>
                <span className={styles.metricSub}>PF, ESIC, Tax & Loans</span>
              </div>
            </div>
          </div>

          {/* Table Controls (Exact Purchase Orders Style & Responsive Grid) */}
          <div className={styles.tableControls}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} size={18} />
              <input
                type="text"
                placeholder="Search by month, year or slip number..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles.filterActions}>
              <div className={styles.perPageSelectWrapper}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => {
                    setPagination(prev => ({ ...prev, per_page: option.value, current_page: 1 }));
                  }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 15, label: '15 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles.filterBtn} ${showFilters ? styles.active : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(selectedYear || statusFilter) && (
                  <span className={styles.filterBadge}></span>
                )}
              </button>
            </div>
          </div>

          {/* Sliding Filter Panel (Exact Purchase Orders Style) */}
          {showFilters && (
            <div className={styles.filtersPanel}>
              <div className={styles.filtersHeader}>
                <div className={styles.filtersTitleGroup}>
                  <h3>Filter Salary Slips</h3>
                  <p>Filter records by year or disbursement status.</p>
                </div>
                <button className={styles.closeIconBtn} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles.filtersGrid}>
                <div className={styles.filterGroup}>
                  <label>Year</label>
                  <CustomSelect
                    value={selectedYear ? { value: selectedYear, label: selectedYear } : null}
                    onChange={(opt) => setSelectedYear(opt ? opt.value : '')}
                    options={yearOptions}
                    placeholder="All Years"
                    isClearable={true}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <label>Status</label>
                  <CustomSelect
                    value={statusFilter ? { value: statusFilter, label: statusFilter.toUpperCase() } : null}
                    onChange={(opt) => setStatusFilter(opt ? opt.value : '')}
                    options={[
                      { value: 'paid', label: 'PAID' },
                      { value: 'approved', label: 'APPROVED' },
                      { value: 'generated', label: 'GENERATED' },
                      { value: 'draft', label: 'DRAFT' }
                    ]}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles.filtersFooter}>
                <button className={styles.resetBtn} onClick={resetFilters}>Reset</button>
                <button className={styles.closeBtn} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          {/* Table & Cards Container */}
          <div className={[styles.customTableContainer, 'card'].join(' ')}>
            {/* Desktop Table View */}
            <div className={styles.customTableWrapper}>
              <table className={styles.customTable}>
                <thead>
                  <tr>
                    <th>MONTH & YEAR</th>
                    <th>SLIP NUMBER</th>
                    <th>ATTENDANCE</th>
                    <th>GROSS SALARY</th>
                    <th>DEDUCTIONS</th>
                    <th>NET TAKE-HOME</th>
                    <th>STATUS</th>
                    <th className={styles.actionsCell}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayrolls.length > 0 ? (
                    filteredPayrolls.map((payroll) => (
                      <tr key={payroll.id}>
                        <td>
                          <div className={styles.monthPill}>
                            <span className={styles.monthBadge}>
                              {getMonthName(payroll.month)}
                            </span>
                            <span>{payroll.year}</span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={styles.slipNumber}
                            onClick={() => setSelectedSlip(payroll)}
                          >
                            {payroll.payroll_number || `SLIP-#${payroll.id}`}
                          </span>
                        </td>
                        <td>
                          <div className={styles.daysInfo}>
                            <span className={styles.daysPresent}>
                              {payroll.present_days || 0} / {payroll.working_days || payroll.total_days_in_month || 0} Days
                            </span>
                            {parseFloat(payroll.ot_days || 0) > 0 && (
                              <span className={styles.daysAbsent}>
                                +{payroll.ot_days} OT Days
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={styles.amountGross}>
                            {formatCurrency(payroll.gross_salary || payroll.earned_salary || payroll.basic_salary)}
                          </span>
                        </td>
                        <td>
                          <span className={styles.amountDeduction}>
                            -{formatCurrency(payroll.total_deductions)}
                          </span>
                        </td>
                        <td>
                          <span className={styles.amountPrimary}>
                            {formatCurrency(payroll.net_salary)}
                          </span>
                        </td>
                        <td>{getStatusBadge(payroll.status)}</td>
                        <td className={styles.actionsCell}>
                          <div className={`action-wrapper ${styles.actionWrapper}`}>
                            <button
                              className={`${styles.actionBtn} ${openMenuIndex === payroll.id ? styles.active : ''}`}
                              onClick={(e) => toggleActionMenu(payroll.id, e)}
                            >
                              <MoreVertical size={18} />
                            </button>

                            {openMenuIndex === payroll.id && (
                              <div
                                className={[styles.actionDropdown, styles.shadowPremium].join(' ')}
                                style={{
                                  position: 'fixed',
                                  top: menuPosition.top,
                                  left: menuPosition.left,
                                  transform: menuPosition.transform || 'none'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  className={styles.dropdownItem}
                                  onClick={() => {
                                    setOpenMenuIndex(null);
                                    setSelectedSlip(payroll);
                                  }}
                                >
                                  <Eye size={16} /> View Details
                                </button>
                                <button
                                  className={styles.dropdownItem}
                                  onClick={() => handlePreviewPdf(payroll)}
                                >
                                  <Printer size={16} /> Print / Preview
                                </button>
                                <div className={styles.dropdownDivider}></div>
                                <button
                                  className={styles.dropdownItem}
                                  onClick={() => handleDownloadPdf(payroll)}
                                  style={{ color: '#ea580c' }}
                                >
                                  <Download size={16} /> Download PDF
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <NoData message="No Salary Slips found." isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile PO Style Cards View */}
            <div className={styles.poCards}>
              {filteredPayrolls.length > 0 ? (
                filteredPayrolls.map((payroll) => (
                  <div key={payroll.id} className={styles.poCard}>
                    {/* Card Main: ID, Net Take-Home and Action Dropdown */}
                    <div className={styles.cardMain}>
                      <div className={styles.cardInfo}>
                        <span
                          className={styles.cardId}
                          onClick={() => setSelectedSlip(payroll)}
                        >
                          {payroll.payroll_number || `SLIP-#${payroll.id}`}
                        </span>
                        <span className={styles.cardAmount}>
                          {formatCurrency(payroll.net_salary)}
                        </span>
                      </div>

                      <div className={`action-wrapper ${styles.actionWrapper}`}>
                        <button
                          className={`${styles.actionBtn} ${openMenuIndex === payroll.id ? styles.active : ''}`}
                          onClick={(e) => toggleActionMenu(payroll.id, e)}
                        >
                          <MoreVertical size={18} />
                        </button>

                        {openMenuIndex === payroll.id && (
                          <div
                            className={[styles.actionDropdown, styles.shadowPremium].join(' ')}
                            style={{
                              position: 'fixed',
                              top: menuPosition.top,
                              left: menuPosition.left,
                              transform: menuPosition.transform || 'none'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className={styles.dropdownItem}
                              onClick={() => {
                                setOpenMenuIndex(null);
                                setSelectedSlip(payroll);
                              }}
                            >
                              <Eye size={16} /> View Details
                            </button>
                            <button
                              className={styles.dropdownItem}
                              onClick={() => handlePreviewPdf(payroll)}
                            >
                              <Printer size={16} /> Print / Preview
                            </button>
                            <div className={styles.dropdownDivider}></div>
                            <button
                              className={styles.dropdownItem}
                              onClick={() => handleDownloadPdf(payroll)}
                              style={{ color: '#ea580c' }}
                            >
                              <Download size={16} /> Download PDF
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card Details: 2-column key-value grid (same as PO) */}
                    <div className={styles.cardDetails}>
                      <div className={styles.detailItem} style={{ gridColumn: 'span 2' }}>
                        <span className={styles.detailLabel}>Period</span>
                        <span className={styles.detailValue}>
                          {getMonthName(payroll.month)} {payroll.year}
                        </span>
                      </div>

                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Attendance</span>
                        <span className={styles.detailValue} style={{ color: '#059669', fontWeight: '700' }}>
                          {payroll.present_days || 0} / {payroll.working_days || payroll.total_days_in_month || 0} Days
                          {parseFloat(payroll.ot_days || 0) > 0 && ` (+${payroll.ot_days} OT)`}
                        </span>
                      </div>

                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Basic CTC</span>
                        <span className={styles.detailValue}>
                          {formatCurrency(payroll.basic_salary)}
                        </span>
                      </div>

                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Gross Earnings</span>
                        <span className={styles.detailValue}>
                          {formatCurrency(payroll.gross_salary || payroll.earned_salary || payroll.basic_salary)}
                        </span>
                      </div>

                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Total Deductions</span>
                        <span className={styles.detailValue} style={{ color: '#ef4444' }}>
                          -{formatCurrency(payroll.total_deductions)}
                        </span>
                      </div>
                    </div>

                    {/* Card Footer: Date on left, Status on right (same as PO) */}
                    <div className={styles.cardFooter}>
                      <div className={styles.footerLeft}>
                        <Calendar size={14} />
                        <span>{getMonthName(payroll.month).slice(0, 3)} {payroll.year}</span>
                      </div>
                      <div className={styles.footerStatuses}>
                        {getStatusBadge(payroll.status)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message="No Salary Slips found." isTable={false} />
              )}
            </div>

            {/* Desktop Pagination */}
            <div className={styles.desktopPagination}>
              {pagination.total > pagination.per_page && (
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  totalItems={pagination.total}
                  perPage={pagination.per_page}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>

          {/* Mobile Pagination */}
          <div className={styles.mobilePagination}>
            {pagination.total > pagination.per_page && (
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.last_page}
                totalItems={pagination.total}
                perPage={pagination.per_page}
                onPageChange={handlePageChange}
              />
            )}
          </div>

          <Footer />
        </main>
      </div>

      {/* Salary Slip Detail Modal */}
      {selectedSlip && (
        <div className={styles.modalOverlay} onClick={() => setSelectedSlip(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>
                <FileText size={20} color="#ea580c" />
                <span>Salary Slip - {getMonthName(selectedSlip.month)} {selectedSlip.year}</span>
              </h2>
              <button className={styles.closeBtnIcon} onClick={() => setSelectedSlip(null)}>
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.payslipPaper}>
                <div className={styles.payslipHeaderRow}>
                  <div>
                    <h2 className={styles.companyTitle}>Hari Om Corporation</h2>
                    <p className={styles.companySub}>Ahmedabad, Gujarat, India</p>
                  </div>
                  <div className={styles.slipTitleBox}>
                    <div className={styles.slipHeading}>Salary Pay Slip</div>
                    <div className={styles.slipMeta}>Slip #: {selectedSlip.payroll_number || `SLIP-${selectedSlip.id}`}</div>
                    <div className={styles.slipMeta}>Period: {getMonthName(selectedSlip.month)} {selectedSlip.year}</div>
                  </div>
                </div>

                {/* Employee Details Strip */}
                <div className={styles.empDetailsGrid}>
                  <div className={styles.empDetailItem}>
                    <span className={styles.empDetailLabel}>Employee Name:</span>
                    <span className={styles.empDetailValue}>{selectedSlip.employee?.employee_name || 'N/A'}</span>
                  </div>
                  <div className={styles.empDetailItem}>
                    <span className={styles.empDetailLabel}>Employee Code:</span>
                    <span className={styles.empDetailValue}>{selectedSlip.employee?.employee_code || 'N/A'}</span>
                  </div>
                  <div className={styles.empDetailItem}>
                    <span className={styles.empDetailLabel}>Department:</span>
                    <span className={styles.empDetailValue}>{selectedSlip.employee?.department?.departement_name || 'N/A'}</span>
                  </div>
                  <div className={styles.empDetailItem}>
                    <span className={styles.empDetailLabel}>Designation:</span>
                    <span className={styles.empDetailValue}>{selectedSlip.employee?.designation?.designation_name || 'N/A'}</span>
                  </div>
                </div>

                {/* Attendance Information */}
                <div className={styles.attendanceStrip}>
                  <div className={styles.attItem}>
                    <label>Total Days</label>
                    <span>{selectedSlip.total_days_in_month || 30}</span>
                  </div>
                  <div className={styles.attItem}>
                    <label>Working Days</label>
                    <span>{selectedSlip.working_days || 0}</span>
                  </div>
                  <div className={styles.attItem}>
                    <label>Present Days</label>
                    <span style={{ color: '#059669' }}>{selectedSlip.present_days || 0}</span>
                  </div>
                  <div className={styles.attItem}>
                    <label>OT Days</label>
                    <span style={{ color: '#7c3aed' }}>{selectedSlip.ot_days || 0}</span>
                  </div>
                </div>

                {/* Breakdown: Earnings vs Deductions */}
                <div className={styles.breakdownGrid}>
                  <div className={styles.breakdownCol}>
                    <div className={styles.breakdownHeader}>Earnings (₹)</div>
                    <div className={styles.breakdownList}>
                      <div className={styles.breakdownRow}>
                        <span>Basic CTC</span>
                        <strong>{formatCurrency(selectedSlip.basic_salary)}</strong>
                      </div>
                      <div className={styles.breakdownRow}>
                        <span>Earned Salary</span>
                        <strong>{formatCurrency(selectedSlip.earned_salary || selectedSlip.basic_salary)}</strong>
                      </div>
                      {parseFloat(selectedSlip.ot_amount || 0) > 0 && (
                        <div className={styles.breakdownRow}>
                          <span>Overtime Pay ({selectedSlip.ot_days} days)</span>
                          <strong>{formatCurrency(selectedSlip.ot_amount)}</strong>
                        </div>
                      )}
                    </div>
                    <div className={styles.breakdownTotalRow}>
                      <span>Gross Earnings</span>
                      <span style={{ color: '#0f172a' }}>
                        {formatCurrency(selectedSlip.gross_salary || selectedSlip.earned_salary || selectedSlip.basic_salary)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.breakdownCol}>
                    <div className={styles.breakdownHeader} style={{ color: '#b91c1c' }}>Deductions (₹)</div>
                    <div className={styles.breakdownList}>
                      <div className={styles.breakdownRow}>
                        <span>PF Deduction</span>
                        <strong style={{ color: '#dc2626' }}>{formatCurrency(selectedSlip.pf_deduction)}</strong>
                      </div>
                      <div className={styles.breakdownRow}>
                        <span>ESIC Deduction</span>
                        <strong style={{ color: '#dc2626' }}>{formatCurrency(selectedSlip.esic_deduction)}</strong>
                      </div>
                      <div className={styles.breakdownRow}>
                        <span>Professional Tax</span>
                        <strong style={{ color: '#dc2626' }}>{formatCurrency(selectedSlip.professional_tax)}</strong>
                      </div>
                      {parseFloat(selectedSlip.loan_emi_deduction || 0) > 0 && (
                        <div className={styles.breakdownRow}>
                          <span>Loan / EMI Recovery</span>
                          <strong style={{ color: '#dc2626' }}>{formatCurrency(selectedSlip.loan_emi_deduction)}</strong>
                        </div>
                      )}
                      {parseFloat(selectedSlip.advance_deduction || 0) > 0 && (
                        <div className={styles.breakdownRow}>
                          <span>Advance Recovery</span>
                          <strong style={{ color: '#dc2626' }}>{formatCurrency(selectedSlip.advance_deduction)}</strong>
                        </div>
                      )}
                      {parseFloat(selectedSlip.other_deduction || 0) > 0 && (
                        <div className={styles.breakdownRow}>
                          <span>Other Deduction</span>
                          <strong style={{ color: '#dc2626' }}>{formatCurrency(selectedSlip.other_deduction)}</strong>
                        </div>
                      )}
                    </div>
                    <div className={styles.breakdownTotalRow}>
                      <span>Total Deductions</span>
                      <span style={{ color: '#dc2626' }}>
                        {formatCurrency(selectedSlip.total_deductions)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Net Pay Banner */}
                <div className={styles.netPayBanner}>
                  <div>
                    <div className={styles.netPayLabel}>Net Take-Home Salary</div>
                    <div style={{ fontSize: '13px', opacity: 0.9 }}>Disbursed via direct account transfer</div>
                  </div>
                  <div className={styles.netPayAmount}>
                    {formatCurrency(selectedSlip.net_salary)}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => setSelectedSlip(null)}>
                Close
              </button>
              <button className={styles.btnSecondary} onClick={() => handlePreviewPdf(selectedSlip)}>
                <Printer size={16} />
                <span>Print / Preview PDF</span>
              </button>
              <button className={styles.btnPrimary} onClick={() => handleDownloadPdf(selectedSlip)}>
                <Download size={16} />
                <span>Download PDF</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Inline Preview Modal */}
      {pdfPreviewUrl && (
        <div className={styles.modalOverlay} onClick={closePdfPreview}>
          <div className={`${styles.modalContent} ${styles.modalPreviewContent}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>
                <Printer size={20} color="#ea580c" />
                <span>Salary Slip Preview - {previewSlipNumber}</span>
              </h2>
              <button className={styles.closeBtnIcon} onClick={closePdfPreview}>
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody} style={{ padding: 0 }}>
              <iframe
                src={pdfPreviewUrl}
                title="Salary Slip PDF Preview"
                className={styles.pdfFrame}
              />
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={closePdfPreview}>
                Close
              </button>
              <button
                className={styles.btnPrimary}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfPreviewUrl;
                  link.setAttribute('download', `Salary_Slip_${previewSlipNumber}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.parentNode.removeChild(link);
                }}
              >
                <Download size={16} />
                <span>Download PDF</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSalarySlips;
